import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Component,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { useForm } from 'react-hook-form'
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import {
  COLOURS,
  MAX_DESCRIPTION,
  MAX_PROJECT_DESCRIPTION,
  MAX_PROJECT_NAME,
  MAX_TITLE,
  QUADRANTS,
  QUADRANT_META,
  type ItemColour,
  type Quadrant,
} from '../shared/constants'
import type { Board, Project, SyncState, Tag, WorkItem } from '../shared/types'
import { slugifyName } from '../shared/slugs'
import { projectNameSchema } from '../shared/schemas'
import { api, ApiError } from './api'
import { authClient } from './auth-client'
import {
  cacheItems,
  cacheProjects,
  drainMutationQueue,
  pendingMutations,
  readCachedItems,
  readCachedProjects,
  resilientMutation,
  retryAllMutations,
} from './offline'

interface PublicConfig {
  signupMode: 'disabled' | 'invite_only' | 'open'
  googleEnabled: boolean
  microsoftEnabled: boolean
  devAuthBypass: boolean
}

function ErrorBoundaryFallback({ error }: { error: Error }) {
  return (
    <main className="login-page">
      <section className="login-card">
        <h1>Something went wrong</h1>
        <p>
          The application hit an unexpected client error. Pending offline changes remain stored.
        </p>
        <div className="error-box">{error.message}</div>
        <p>
          <button className="button primary" onClick={() => location.reload()}>
            Reload
          </button>
        </p>
      </section>
    </main>
  )
}

class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  render() {
    return this.state.error ? (
      <ErrorBoundaryFallback error={this.state.error} />
    ) : (
      this.props.children
    )
  }
}

export function App() {
  const session = authClient.useSession()
  const config = useQuery({
    queryKey: ['public-config'],
    queryFn: () => api<PublicConfig>('/config'),
    retry: false,
  })

  if (session.isPending || config.isPending) {
    return (
      <main className="login-page">
        <div aria-label="Loading nownextnever">Loading…</div>
      </main>
    )
  }
  if (!session.data && !config.data?.devAuthBypass) return <Login config={config.data} />
  return (
    <AppErrorBoundary>
      <Workspace config={config.data} />
    </AppErrorBoundary>
  )
}

function Login({ config }: { config?: PublicConfig }) {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('alex@example.test')
  const [password, setPassword] = useState('LocalPassphrase123!')
  const [name, setName] = useState('')
  const [invitation, setInvitation] = useState(
    () => new URLSearchParams(location.search).get('invite') ?? '',
  )
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const signupAllowed = config?.signupMode !== 'disabled'

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      if (mode === 'signin') {
        const result = await authClient.signIn.email({ email, password, rememberMe: true })
        if (result.error) throw new Error(result.error.message)
      } else {
        const response = await fetch('/api/auth/sign-up/email', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(invitation ? { 'x-invitation-token': invitation } : {}),
          },
          body: JSON.stringify({ email, password, name }),
        })
        const body = await response.json().catch(() => null)
        if (!response.ok) throw new Error(body?.message ?? body?.error?.message ?? 'Sign up failed')
        location.assign('/')
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Authentication failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="login-page">
      <section className="login-card" aria-labelledby="login-title">
        <h1 id="login-title">
          nownextnever<span className="brand-dots">.</span>
        </h1>
        <p>
          {mode === 'signin' ? 'Your priorities, clearly placed.' : 'Create your private matrix.'}
        </p>
        <form className="form-grid" onSubmit={submit}>
          {mode === 'signup' && (
            <label className="label">
              Name
              <input
                className="field"
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
            </label>
          )}
          <label className="label">
            Email
            <input
              className="field"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </label>
          <label className="label">
            Password
            <input
              className="field"
              required
              minLength={10}
              maxLength={128}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            />
          </label>
          {mode === 'signup' && config?.signupMode === 'invite_only' && (
            <label className="label">
              Invitation token
              <input
                className="field"
                required
                value={invitation}
                onChange={(e) => setInvitation(e.target.value)}
                autoComplete="off"
              />
            </label>
          )}
          {error && (
            <div className="error-box" role="alert">
              {error}
            </div>
          )}
          <button className="button primary" disabled={busy}>
            {busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        </form>
        {signupAllowed && (
          <p style={{ marginTop: '1rem' }}>
            <button
              className="button"
              onClick={() => {
                setMode(mode === 'signin' ? 'signup' : 'signin')
                setError('')
              }}
            >
              {mode === 'signin' ? 'Use an invitation' : 'Back to sign in'}
            </button>
          </p>
        )}
        {(config?.googleEnabled || config?.microsoftEnabled) && (
          <div className="form-grid" style={{ marginTop: '1rem' }}>
            {config.googleEnabled && (
              <button
                className="button"
                onClick={() => authClient.signIn.social({ provider: 'google' })}
              >
                Continue with Google
              </button>
            )}
            {config.microsoftEnabled && (
              <button
                className="button"
                onClick={() => authClient.signIn.social({ provider: 'microsoft' })}
              >
                Continue with Microsoft
              </button>
            )}
          </div>
        )}
      </section>
    </main>
  )
}

type EditorState = { mode: 'new'; quadrant: Quadrant } | { mode: 'edit'; item: WorkItem } | null
type CardMenuState = { item: WorkItem; top: number; right: number } | null
type ProjectEditorState = { mode: 'new' } | { mode: 'edit'; project: Project } | null

export function parseQuickCreate(value: string, projects: Project[]) {
  const match = value.match(/(?:^|\s)@([\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*)/u)
  const project = match
    ? projects.find(
        (candidate) =>
          candidate.closedAt === null && candidate.slug.toLowerCase() === match[1].toLowerCase(),
      )
    : undefined
  return {
    title:
      project && match ? value.replace(match[0], ' ').replace(/\s+/g, ' ').trim() : value.trim(),
    projectId: project?.id ?? null,
  }
}

function Workspace({ config }: { config?: PublicConfig }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const searchRef = useRef<HTMLInputElement>(null)
  const [boardId, setBoardId] = useState(() => localStorage.getItem('active-board') ?? '')
  const [editor, setEditor] = useState<EditorState>(null)
  const [projectEditor, setProjectEditor] = useState<ProjectEditorState>(null)
  const [search, setSearch] = useState('')
  const [syncState, setSyncState] = useState<SyncState>('saved')
  const [syncCount, setSyncCount] = useState(0)
  const [conflict, setConflict] = useState<WorkItem | null>(null)
  const [undoItem, setUndoItem] = useState<WorkItem | null>(null)
  const [undoDeleted, setUndoDeleted] = useState<WorkItem | null>(null)
  const [selectedTagId, setSelectedTagId] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [cardMenu, setCardMenu] = useState<CardMenuState>(null)

  const boardsQuery = useQuery({ queryKey: ['boards'], queryFn: () => api<Board[]>('/boards') })
  useEffect(() => {
    if (!boardsQuery.data?.length) return
    if (!boardsQuery.data.some((board) => board.id === boardId)) setBoardId(boardsQuery.data[0].id)
  }, [boardsQuery.data, boardId])
  useEffect(() => {
    if (boardId) localStorage.setItem('active-board', boardId)
  }, [boardId])

  const itemsQuery = useQuery({
    queryKey: ['items', boardId],
    enabled: Boolean(boardId),
    queryFn: async () => {
      try {
        const items = await api<WorkItem[]>(`/boards/${boardId}/items?limit=1000`)
        await cacheItems(boardId, items)
        return items
      } catch (error) {
        const cached = await readCachedItems(boardId)
        if (cached.length || !navigator.onLine) {
          setSyncState('offline')
          return cached
        }
        throw error
      }
    },
  })
  const tagsQuery = useQuery({
    queryKey: ['tags', boardId],
    enabled: Boolean(boardId),
    queryFn: () => api<Tag[]>(`/boards/${boardId}/tags`),
  })
  const projectsQuery = useQuery({
    queryKey: ['projects', boardId],
    enabled: Boolean(boardId),
    queryFn: async () => {
      try {
        const projects = await api<Project[]>(`/boards/${boardId}/projects`)
        await cacheProjects(boardId, projects)
        return projects
      } catch (error) {
        const cached = await readCachedProjects(boardId)
        if (cached.length || !navigator.onLine) return cached
        throw error
      }
    },
  })
  useEffect(() => {
    if (!selectedTagId || !tagsQuery.data) return
    if (!tagsQuery.data.some((tag) => tag.id === selectedTagId)) setSelectedTagId('')
  }, [selectedTagId, tagsQuery.data])
  useEffect(() => {
    if (!selectedProjectId || !projectsQuery.data) return
    if (!projectsQuery.data.some((project) => project.id === selectedProjectId))
      setSelectedProjectId('')
  }, [selectedProjectId, projectsQuery.data])

  const refreshQueue = useCallback(async () => {
    const mutations = await pendingMutations()
    setSyncCount(mutations.length)
    if (!navigator.onLine) setSyncState(mutations.length ? 'offline' : 'saved')
    else if (mutations.some((item) => item.state === 'conflict')) setSyncState('conflict')
    else if (mutations.some((item) => item.state === 'failed')) setSyncState('failed')
    else if (mutations.length) setSyncState('saving')
    else setSyncState('saved')
  }, [])

  useEffect(() => {
    const sync = async () => {
      await drainMutationQueue(() => queryClient.invalidateQueries({ queryKey: ['items'] }))
      await refreshQueue()
    }
    void sync()
    const interval = window.setInterval(sync, 15_000)
    window.addEventListener('online', sync)
    window.addEventListener('offline', refreshQueue)
    return () => {
      clearInterval(interval)
      window.removeEventListener('online', sync)
      window.removeEventListener('offline', refreshQueue)
    }
  }, [queryClient, refreshQueue])

  useEffect(() => {
    function keyboard(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null
      const editing = target?.matches('input, textarea, select, [contenteditable="true"]')
      if (event.key === 'Escape') {
        setEditor(null)
        setProjectEditor(null)
        setConflict(null)
        setCardMenu(null)
      }
      if (!editing && event.key.toLowerCase() === 'n') {
        event.preventDefault()
        setEditor({ mode: 'new', quadrant: 'DO_FIRST' })
      }
      if (!editing && event.key === '/') {
        event.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', keyboard)
    return () => window.removeEventListener('keydown', keyboard)
  }, [])

  useEffect(() => {
    if (!cardMenu) return
    function closeOnOutsideClick(event: PointerEvent) {
      const target = event.target as HTMLElement | null
      if (target?.closest('.floating-card-menu, .card-menu-trigger')) return
      setCardMenu(null)
    }
    function closeOnViewportChange() {
      setCardMenu(null)
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    window.addEventListener('resize', closeOnViewportChange)
    window.addEventListener('scroll', closeOnViewportChange, true)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      window.removeEventListener('resize', closeOnViewportChange)
      window.removeEventListener('scroll', closeOnViewportChange, true)
    }
  }, [cardMenu])

  const allItems = itemsQuery.data ?? []
  const activeItems = allItems.filter((item) => item.status === 'active' && !item.deletedAt)
  const completedItems = allItems.filter((item) => item.status === 'completed' && !item.deletedAt)
  const itemMatchesSearch = (item: WorkItem) =>
    `${item.title} ${item.description} ${item.tags.map((tag) => tag.name).join(' ')}`
      .toLowerCase()
      .includes(search.toLowerCase())
  const itemMatchesTag = (item: WorkItem) =>
    !selectedTagId || item.tags.some((tag) => tag.id === selectedTagId)
  const itemMatchesProject = (item: WorkItem) =>
    !selectedProjectId || item.projectId === selectedProjectId
  const filteredActive = activeItems.filter(
    (item) => itemMatchesSearch(item) && itemMatchesTag(item) && itemMatchesProject(item),
  )
  const setItems = (updater: (items: WorkItem[]) => WorkItem[]) =>
    queryClient.setQueryData<WorkItem[]>(['items', boardId], (old = []) => updater(old))

  async function createTag(name: string): Promise<Tag> {
    const tag = await api<Tag>(`/boards/${boardId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ name, colour: 'slate' }),
    })
    await queryClient.invalidateQueries({ queryKey: ['tags', boardId] })
    return tag
  }

  async function saveProject(values: ProjectEditorValues) {
    if (projectEditor?.mode === 'edit') {
      await api<Project>(`/projects/${projectEditor.project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...values, version: projectEditor.project.version }),
      })
    } else {
      await api<Project>(`/boards/${boardId}/projects`, {
        method: 'POST',
        body: JSON.stringify(values),
      })
    }
    await queryClient.invalidateQueries({ queryKey: ['projects', boardId] })
  }

  async function toggleProjectClosed(project: Project) {
    const action = project.closedAt ? 'reopen' : 'close'
    await api<Project>(`/projects/${project.id}/${action}`, {
      method: 'POST',
      body: JSON.stringify({ version: project.version }),
    })
    await queryClient.invalidateQueries({ queryKey: ['projects', boardId] })
  }

  async function runItemMutation(options: {
    method: 'POST' | 'PATCH' | 'DELETE'
    path: string
    body?: unknown
    optimistic: WorkItem
    remove?: boolean
  }) {
    setSyncState('saving')
    if (options.remove)
      setItems((items) => items.filter((item) => item.id !== options.optimistic.id))
    else
      setItems((items) => [
        ...items.filter((item) => item.id !== options.optimistic.id),
        options.optimistic,
      ])
    try {
      const result = await resilientMutation<WorkItem>({ ...options, id: crypto.randomUUID() })
      if (!result.queued && !options.remove)
        setItems((items) => [...items.filter((item) => item.id !== result.value.id), result.value])
      setSyncState(result.queued ? 'offline' : 'saved')
      await refreshQueue()
      if (!result.queued) await queryClient.invalidateQueries({ queryKey: ['items', boardId] })
      return result.value
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setConflict((error.details as any)?.current ?? null)
        setSyncState('conflict')
      } else setSyncState('failed')
      await queryClient.invalidateQueries({ queryKey: ['items', boardId] })
      throw error
    }
  }

  async function createItem(values: EditorValues) {
    const now = Date.now()
    const selectedTags = (tagsQuery.data ?? []).filter((tag) => values.tagIds.includes(tag.id))
    const optimistic: WorkItem = {
      id: crypto.randomUUID(),
      boardId,
      createdByUserId: 'pending',
      title: values.title,
      description: values.description,
      colour: values.colour,
      quadrant: values.quadrant,
      position:
        Math.max(
          0,
          ...activeItems.filter((i) => i.quadrant === values.quadrant).map((i) => i.position),
        ) + 1024,
      status: 'active',
      version: 1,
      createdAt: now,
      updatedAt: now,
      completedAt: null,
      deletedAt: null,
      projectId: values.projectId,
      tags: selectedTags,
    }
    await runItemMutation({
      method: 'POST',
      path: `/boards/${boardId}/items`,
      body: {
        id: optimistic.id,
        title: values.title,
        description: values.description,
        colour: values.colour,
        quadrant: values.quadrant,
        tagIds: values.tagIds,
        projectId: values.projectId,
      },
      optimistic,
    })
  }
  async function updateItem(item: WorkItem, values: EditorValues) {
    const selectedTags = (tagsQuery.data ?? []).filter((tag) => values.tagIds.includes(tag.id))
    const optimistic = {
      ...item,
      title: values.title,
      description: values.description,
      colour: values.colour,
      quadrant: values.quadrant,
      projectId: values.projectId,
      tags: selectedTags,
      version: item.version + 1,
      updatedAt: Date.now(),
    }
    await runItemMutation({
      method: 'PATCH',
      path: `/items/${item.id}`,
      body: { ...values, version: item.version },
      optimistic,
    })
  }
  async function assignProject(item: WorkItem, projectId: string | null) {
    const optimistic = {
      ...item,
      projectId,
      version: item.version + 1,
      updatedAt: Date.now(),
    }
    await runItemMutation({
      method: 'PATCH',
      path: `/items/${item.id}`,
      body: { projectId, version: item.version },
      optimistic,
    })
  }
  async function completeItem(item: WorkItem) {
    const optimistic = {
      ...item,
      status: 'completed' as const,
      completedAt: Date.now(),
      version: item.version + 1,
    }
    await runItemMutation({
      method: 'POST',
      path: `/items/${item.id}/complete`,
      body: { version: item.version },
      optimistic,
    })
    setUndoItem(item)
  }
  async function restoreItem(item: WorkItem) {
    const optimistic = {
      ...item,
      status: 'active' as const,
      completedAt: null,
      version: item.version + 1,
    }
    await runItemMutation({
      method: 'POST',
      path: `/items/${item.id}/restore`,
      body: { version: item.version },
      optimistic,
    })
  }
  async function deleteItem(item: WorkItem) {
    if (!confirm(`Move “${item.title}” to trash?`)) return
    const optimistic = { ...item, deletedAt: Date.now(), version: item.version + 1 }
    const deleted = await runItemMutation({
      method: 'DELETE',
      path: `/items/${item.id}`,
      body: { version: item.version },
      optimistic,
    })
    setUndoDeleted(deleted)
  }
  async function undeleteItem(item: WorkItem) {
    const optimistic = { ...item, deletedAt: null, version: item.version + 1 }
    await runItemMutation({
      method: 'POST',
      path: `/items/${item.id}/undelete`,
      body: { version: item.version },
      optimistic,
    })
  }
  async function permanentlyDeleteItem(item: WorkItem) {
    if (!confirm(`Permanently delete “${item.title}”? This cannot be undone.`)) return
    await api(`/items/${item.id}?permanent=true`, { method: 'DELETE' })
    setItems((items) => items.filter((candidate) => candidate.id !== item.id))
  }
  async function moveItem(
    item: WorkItem,
    quadrant: Quadrant,
    beforeId?: string | null,
    afterId?: string | null,
  ) {
    if (item.quadrant === quadrant && !beforeId && !afterId) return
    const optimistic = { ...item, quadrant, version: item.version + 1, updatedAt: Date.now() }
    await runItemMutation({
      method: 'POST',
      path: `/boards/${boardId}/items/reorder`,
      body: {
        itemId: item.id,
        quadrant,
        beforeId: beforeId ?? null,
        afterId: afterId ?? null,
        version: item.version,
      },
      optimistic,
    })
  }

  async function addBoard() {
    const name = prompt('Board name')?.trim()
    if (!name) return
    const board = await api<Board>('/boards', { method: 'POST', body: JSON.stringify({ name }) })
    await queryClient.invalidateQueries({ queryKey: ['boards'] })
    setBoardId(board.id)
  }
  async function renameBoard() {
    const current = boardsQuery.data?.find((board) => board.id === boardId)
    const name = prompt('Rename board', current?.name)?.trim()
    if (!name) return
    await api(`/boards/${boardId}`, { method: 'PATCH', body: JSON.stringify({ name }) })
    await queryClient.invalidateQueries({ queryKey: ['boards'] })
  }
  async function deleteBoard() {
    const current = boardsQuery.data?.find((board) => board.id === boardId)
    if (!current || !confirm(`Delete “${current.name}” and all its items? This cannot be undone.`))
      return
    await api(`/boards/${boardId}`, { method: 'DELETE' })
    setBoardId('')
    await queryClient.invalidateQueries({ queryKey: ['boards'] })
  }
  async function exportData() {
    const data = await api<any>('/export')
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `nownextnever-${new Date().toISOString().slice(0, 10)}.json`
    link.click()
    URL.revokeObjectURL(link.href)
  }
  async function importData(file: File) {
    const payload = JSON.parse(await file.text())
    const preview = await api<{ boardCount: number; projectCount: number; itemCount: number }>(
      '/import/validate',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    )
    if (
      !confirm(
        `Import ${preview.boardCount} boards, ${preview.projectCount} projects, and ${preview.itemCount} items as new data?`,
      )
    )
      return
    await api('/import', { method: 'POST', body: JSON.stringify(payload) })
    await queryClient.invalidateQueries({ queryKey: ['boards'] })
  }

  const syncLabels: Record<SyncState, string> = {
    saved: 'Saved',
    saving: 'Saving…',
    offline: `Offline — ${syncCount || ''} changes pending`,
    failed: 'Save failed — retry',
    conflict: 'Conflict — review required',
  }
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  function dragEnd(event: DragEndEvent) {
    const item = activeItems.find((candidate) => candidate.id === event.active.id)
    if (!item || !event.over) return
    const overId = String(event.over.id)
    const overItem = activeItems.find((candidate) => candidate.id === overId)
    const targetQuadrant = overId.startsWith('quadrant-')
      ? (overId.replace('quadrant-', '') as Quadrant)
      : (overItem?.quadrant ?? item.quadrant)
    const target = activeItems
      .filter((candidate) => candidate.quadrant === targetQuadrant && candidate.id !== item.id)
      .sort((a, b) => a.position - b.position)
    const index = overItem
      ? Math.max(
          0,
          target.findIndex((candidate) => candidate.id === overItem.id),
        )
      : target.length
    void moveItem(item, targetQuadrant, target[index - 1]?.id ?? null, target[index]?.id ?? null)
  }

  if (boardsQuery.isError) return <ErrorBoundaryFallback error={boardsQuery.error as Error} />
  return (
    <div className="app-shell">
      {config?.devAuthBypass && (
        <div className="bypass-banner">Development authentication bypass is active</div>
      )}
      <header className="topbar">
        <div className="brand">
          nownextnever<span className="brand-dots">.</span>
        </div>
        <select
          className="field board-select"
          aria-label="Active board"
          style={{ width: 'auto', minWidth: '10rem' }}
          value={boardId}
          onChange={(e) => setBoardId(e.target.value)}
        >
          {boardsQuery.data?.map((board) => (
            <option key={board.id} value={board.id}>
              {board.name}
            </option>
          ))}
        </select>
        <button className="button icon" onClick={addBoard} aria-label="Create board">
          ＋ board
        </button>
        <details className="card-menu">
          <summary className="button icon" aria-label="Board and data menu">
            •••
          </summary>
          <div className="menu-popover">
            <button onClick={renameBoard}>Rename board</button>
            <button onClick={deleteBoard}>Delete board</button>
            <button onClick={exportData}>Export JSON</button>
            <label
              style={{ display: 'block', padding: '.45rem', fontSize: '.82rem', cursor: 'pointer' }}
            >
              Import JSON
              <input
                className="screen-reader"
                type="file"
                accept="application/json"
                onChange={(e) => e.target.files?.[0] && void importData(e.target.files[0])}
              />
            </label>
          </div>
        </details>
        <div className="topbar-spacer" />
        <div className="sync" data-state={syncState} role="status">
          <span className="sync-dot" /> <span>{syncLabels[syncState]}</span>
          {(syncState === 'failed' || syncState === 'offline') && (
            <button
              className="button icon"
              onClick={async () => {
                setSyncState('saving')
                await retryAllMutations()
                await refreshQueue()
                await queryClient.invalidateQueries({ queryKey: ['items'] })
              }}
            >
              Retry
            </button>
          )}
        </div>
        <button
          className="button primary"
          onClick={() => setEditor({ mode: 'new', quadrant: 'DO_FIRST' })}
        >
          ＋ Add
        </button>
        {!config?.devAuthBypass && (
          <button
            className="button"
            onClick={async () => {
              await authClient.signOut()
              location.assign('/')
            }}
          >
            Sign out
          </button>
        )}
      </header>
      <nav className="nav-tabs" aria-label="Views">
        <NavLink className="nav-link" to="/matrix">
          Matrix
        </NavLink>
        <NavLink className="nav-link" to="/list">
          List
        </NavLink>
        <NavLink className="nav-link" to="/completed">
          Completed <span className="count">{completedItems.length}</span>
        </NavLink>
        <NavLink className="nav-link" to="/projects">
          Projects{' '}
          <span className="count">
            {projectsQuery.data?.filter((p) => !p.closedAt).length ?? 0}
          </span>
        </NavLink>
      </nav>
      <section className="filter-bar" aria-label="Item filters">
        <input
          ref={searchRef}
          className="field"
          style={{ maxWidth: '22rem', padding: '.4rem .6rem' }}
          placeholder="Search /"
          aria-label="Search items"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="field"
          style={{ width: 'auto', maxWidth: '14rem', padding: '.4rem .6rem' }}
          aria-label="Filter by tag"
          value={selectedTagId}
          onChange={(e) => setSelectedTagId(e.target.value)}
        >
          <option value="">All tags</option>
          {tagsQuery.data?.map((tag) => (
            <option key={tag.id} value={tag.id}>
              {tag.name}
            </option>
          ))}
        </select>
        <select
          className="field"
          style={{ width: 'auto', maxWidth: '16rem', padding: '.4rem .6rem' }}
          aria-label="Filter by project"
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
        >
          <option value="">All projects</option>
          {projectsQuery.data?.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
              {project.closedAt ? ' (closed)' : ''}
            </option>
          ))}
        </select>
      </section>
      <main className="content">
        {itemsQuery.isLoading && <div className="empty">Loading matrix…</div>}
        {itemsQuery.isError && (
          <div className="error-box" role="alert">
            {(itemsQuery.error as Error).message}
          </div>
        )}
        <DndContext sensors={sensors} onDragEnd={dragEnd}>
          <Routes>
            <Route path="/" element={<Navigate to="/matrix" replace />} />
            <Route
              path="/matrix"
              element={
                <Matrix
                  items={filteredActive}
                  projects={projectsQuery.data ?? []}
                  onAdd={(quadrant) => setEditor({ mode: 'new', quadrant })}
                  onEdit={(item) => setEditor({ mode: 'edit', item })}
                  onComplete={completeItem}
                  onOpenMenu={(item, rect) =>
                    setCardMenu((current) =>
                      current?.item.id === item.id
                        ? null
                        : {
                            item,
                            top: rect.bottom + 6,
                            right: Math.max(8, window.innerWidth - rect.right),
                          },
                    )
                  }
                  onQuickAdd={async (title, quadrant) =>
                    createItem({
                      ...parseQuickCreate(title, projectsQuery.data ?? []),
                      description: '',
                      colour: 'amber',
                      quadrant,
                      tagIds: [],
                    })
                  }
                />
              }
            />
            <Route
              path="/list"
              element={
                <ListView
                  items={filteredActive}
                  onEdit={(item) => setEditor({ mode: 'edit', item })}
                />
              }
            />
            <Route
              path="/completed"
              element={
                <CompletedView
                  items={completedItems
                    .filter((item) => itemMatchesSearch(item) && itemMatchesTag(item))
                    .filter((item) => itemMatchesProject(item))}
                  onRestore={restoreItem}
                  onDelete={permanentlyDeleteItem}
                />
              }
            />
            <Route
              path="/projects"
              element={
                <ProjectsView
                  projects={projectsQuery.data ?? []}
                  items={allItems.filter((item) => !item.deletedAt)}
                  selectedProjectId={selectedProjectId}
                  onCreate={() => setProjectEditor({ mode: 'new' })}
                  onEdit={(project) => setProjectEditor({ mode: 'edit', project })}
                  onToggleClosed={toggleProjectClosed}
                  onAssign={assignProject}
                  onShowItems={(projectId) => {
                    setSelectedProjectId(projectId)
                    navigate('/matrix')
                  }}
                />
              }
            />
            <Route path="*" element={<Navigate to="/matrix" replace />} />
          </Routes>
        </DndContext>
      </main>
      {editor && (
        <ItemEditor
          state={editor}
          onClose={() => setEditor(null)}
          onSave={async (values) => {
            if (editor.mode === 'new') await createItem(values)
            else await updateItem(editor.item, values)
            setEditor(null)
          }}
          tags={tagsQuery.data ?? []}
          projects={projectsQuery.data ?? []}
          onCreateTag={createTag}
        />
      )}
      {projectEditor && (
        <ProjectEditor
          state={projectEditor}
          onClose={() => setProjectEditor(null)}
          onSave={async (values) => {
            await saveProject(values)
            setProjectEditor(null)
          }}
        />
      )}
      {cardMenu && (
        <CardActionMenu
          state={cardMenu}
          onClose={() => setCardMenu(null)}
          onEdit={(item) => {
            setCardMenu(null)
            setEditor({ mode: 'edit', item })
          }}
          onComplete={async (item) => {
            setCardMenu(null)
            await completeItem(item)
          }}
          onDelete={async (item) => {
            setCardMenu(null)
            await deleteItem(item)
          }}
          onMove={async (item, quadrant) => {
            setCardMenu(null)
            await moveItem(item, quadrant)
          }}
        />
      )}
      {conflict && (
        <div
          className="panel-backdrop"
          onMouseDown={(e) => e.target === e.currentTarget && setConflict(null)}
        >
          <section
            className="panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="conflict-title"
          >
            <div className="panel-head">
              <h2 id="conflict-title">This item changed elsewhere</h2>
              <button className="button" onClick={() => setConflict(null)}>
                Close
              </button>
            </div>
            <div className="conflict-box">
              Your edit was not silently overwritten. The newest server copy is shown below; refresh
              it before retrying.
            </div>
            <h3>{conflict.title}</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{conflict.description || 'No description'}</p>
            <button
              className="button primary"
              onClick={async () => {
                setConflict(null)
                await queryClient.invalidateQueries({ queryKey: ['items', boardId] })
                setEditor({ mode: 'edit', item: conflict })
              }}
            >
              Use server copy
            </button>
          </section>
        </div>
      )}
      {undoItem && (
        <div className="toast" role="status">
          Completed “{undoItem.title}”{' '}
          <button
            onClick={async () => {
              const latest = (queryClient.getQueryData<WorkItem[]>(['items', boardId]) ?? []).find(
                (item) => item.id === undoItem.id,
              )
              if (latest) await restoreItem(latest)
              setUndoItem(null)
            }}
          >
            Undo
          </button>
          <button aria-label="Dismiss" onClick={() => setUndoItem(null)}>
            ×
          </button>
        </div>
      )}
      {undoDeleted && (
        <div className="toast" role="status">
          Moved “{undoDeleted.title}” to trash
          <button
            onClick={async () => {
              await undeleteItem(undoDeleted)
              setUndoDeleted(null)
            }}
          >
            Undo
          </button>
          <button aria-label="Dismiss" onClick={() => setUndoDeleted(null)}>
            ×
          </button>
        </div>
      )}
    </div>
  )
}

const projectEditorSchema = z.object({
  name: projectNameSchema,
  description: z.string().max(MAX_PROJECT_DESCRIPTION),
})
type ProjectEditorValues = z.infer<typeof projectEditorSchema>

function ProjectEditor({
  state,
  onClose,
  onSave,
}: {
  state: Exclude<ProjectEditorState, null>
  onClose: () => void
  onSave: (values: ProjectEditorValues) => Promise<void>
}) {
  const project = state.mode === 'edit' ? state.project : null
  const form = useForm<ProjectEditorValues>({
    resolver: zodResolver(projectEditorSchema),
    defaultValues: {
      name: project?.name ?? '',
      description: project?.description ?? '',
    },
  })
  const name = form.watch('name')
  const description = form.watch('description')
  const slug = slugifyName(name)

  return (
    <div
      className="panel-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className="panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-editor-title"
      >
        <div className="panel-head">
          <h2 id="project-editor-title">{project ? 'Edit project' : 'New project'}</h2>
          <button className="button icon" onClick={onClose} aria-label="Close project editor">
            ×
          </button>
        </div>
        <form
          className="form-grid"
          onSubmit={form.handleSubmit(async (values) => {
            try {
              await onSave(values)
            } catch (error) {
              form.setError('root', {
                message: error instanceof Error ? error.message : 'Could not save project',
              })
            }
          })}
        >
          <label className="label">
            Name
            <input
              autoFocus
              className="field"
              maxLength={MAX_PROJECT_NAME}
              {...form.register('name')}
            />
            {form.formState.errors.name && <small>{form.formState.errors.name.message}</small>}
          </label>
          <div className="project-slug-preview" aria-live="polite">
            <span>Quick-add shorthand</span>
            <code>@{slug || 'project-slug'}</code>
          </div>
          <label className="label">
            Description
            <textarea
              className="field textarea"
              maxLength={MAX_PROJECT_DESCRIPTION}
              {...form.register('description')}
            />
            <span className="muted" style={{ fontWeight: 400 }}>
              {description?.length ?? 0}/{MAX_PROJECT_DESCRIPTION}
            </span>
          </label>
          {form.formState.errors.root && (
            <div className="error-box" role="alert">
              {form.formState.errors.root.message}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button className="button primary" disabled={form.formState.isSubmitting || !slug}>
              {form.formState.isSubmitting
                ? 'Saving…'
                : project
                  ? 'Save project'
                  : 'Create project'}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

function ProjectsView({
  projects,
  items,
  selectedProjectId,
  onCreate,
  onEdit,
  onToggleClosed,
  onAssign,
  onShowItems,
}: {
  projects: Project[]
  items: WorkItem[]
  selectedProjectId: string
  onCreate: () => void
  onEdit: (project: Project) => void
  onToggleClosed: (project: Project) => Promise<void>
  onAssign: (item: WorkItem, projectId: string | null) => Promise<void>
  onShowItems: (projectId: string) => void
}) {
  const openProjects = projects.filter((project) => !project.closedAt)
  const closedProjects = projects.filter((project) => project.closedAt)
  return (
    <section aria-labelledby="projects-title">
      <div className="section-heading">
        <div>
          <h1 id="projects-title">Projects</h1>
          <p className="muted">
            Streams of work within this board. Use a project’s @slug in quick add.
          </p>
        </div>
        <button className="button primary" onClick={onCreate}>
          ＋ Project
        </button>
      </div>

      <div className="project-grid">
        {[...openProjects, ...closedProjects].map((project) => {
          const projectItems = items.filter((item) => item.projectId === project.id)
          return (
            <article
              className={`project-card ${selectedProjectId === project.id ? 'selected' : ''}`}
              key={project.id}
            >
              <div className="project-card-head">
                <div>
                  <h2>{project.name}</h2>
                  <code>@{project.slug}</code>
                </div>
                {project.closedAt && <span className="status-pill">Closed</span>}
              </div>
              {project.description && <p>{project.description}</p>}
              <div className="project-counts">
                {QUADRANTS.map((quadrant) => (
                  <span key={quadrant}>
                    {QUADRANT_META[quadrant].short}{' '}
                    <strong>
                      {
                        projectItems.filter(
                          (item) => item.status === 'active' && item.quadrant === quadrant,
                        ).length
                      }
                    </strong>
                  </span>
                ))}
                <span>
                  Done{' '}
                  <strong>
                    {projectItems.filter((item) => item.status === 'completed').length}
                  </strong>
                </span>
              </div>
              <div className="project-actions">
                <button className="button" onClick={() => onShowItems(project.id)}>
                  Show items
                </button>
                <button className="button" onClick={() => onEdit(project)}>
                  Edit
                </button>
                <button className="button" onClick={() => void onToggleClosed(project)}>
                  {project.closedAt ? 'Re-open' : 'Close'}
                </button>
              </div>
            </article>
          )
        })}
        {!projects.length && (
          <div className="empty">Create a project to group related work across quadrants.</div>
        )}
      </div>

      <h2 style={{ marginTop: '2rem' }}>Associate existing items</h2>
      <p className="muted">
        Assignments save immediately and can also be changed in the item editor.
      </p>
      <div className="table-wrap">
        <table className="item-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>State</th>
              <th>Project</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const current = projects.find((project) => project.id === item.projectId)
              return (
                <tr key={item.id}>
                  <td>
                    <strong>{item.title}</strong>
                  </td>
                  <td>
                    {item.status === 'completed' ? 'Completed' : QUADRANT_META[item.quadrant].title}
                  </td>
                  <td>
                    <select
                      className="field"
                      aria-label={`Project for ${item.title}`}
                      value={item.projectId ?? ''}
                      onChange={(event) => void onAssign(item, event.target.value || null)}
                    >
                      <option value="">No project</option>
                      {openProjects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                      {current?.closedAt && (
                        <option value={current.id}>{current.name} (closed)</option>
                      )}
                    </select>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {!items.length && <div className="empty">No items to associate yet.</div>}
      </div>
    </section>
  )
}

function Matrix(props: {
  items: WorkItem[]
  projects: Project[]
  onAdd: (quadrant: Quadrant) => void
  onEdit: (item: WorkItem) => void
  onComplete: (item: WorkItem) => void
  onOpenMenu: (item: WorkItem, rect: DOMRect) => void
  onQuickAdd: (title: string, quadrant: Quadrant) => Promise<void>
}) {
  const { items, ...handlers } = props
  return (
    <div className="matrix">
      {QUADRANTS.map((quadrant) => (
        <QuadrantView
          key={quadrant}
          quadrant={quadrant}
          items={items
            .filter((item) => item.quadrant === quadrant)
            .sort((a, b) => a.position - b.position)}
          {...handlers}
        />
      ))}
    </div>
  )
}

function QuadrantView({
  quadrant,
  items,
  projects,
  onAdd,
  onEdit,
  onComplete,
  onOpenMenu,
  onQuickAdd,
}: {
  quadrant: Quadrant
  items: WorkItem[]
  projects: Project[]
  onAdd: (q: Quadrant) => void
  onEdit: (item: WorkItem) => void
  onComplete: (item: WorkItem) => void
  onOpenMenu: (item: WorkItem, rect: DOMRect) => void
  onQuickAdd: (title: string, q: Quadrant) => Promise<void>
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `quadrant-${quadrant}` })
  const [title, setTitle] = useState('')
  const shorthand = title.match(/(?:^|\s)@([\p{L}\p{N}-]*)$/u)
  const projectSuggestions = shorthand
    ? projects
        .filter(
          (project) =>
            !project.closedAt && project.slug.toLowerCase().startsWith(shorthand[1].toLowerCase()),
        )
        .slice(0, 5)
    : []
  return (
    <section
      ref={setNodeRef}
      className={`quadrant ${isOver ? 'over' : ''}`}
      data-quadrant={quadrant}
      aria-labelledby={`heading-${quadrant}`}
    >
      <header className="quadrant-head">
        <div>
          <h2 id={`heading-${quadrant}`} className="quadrant-title">
            {QUADRANT_META[quadrant].title}
          </h2>
          <div className="quadrant-subtitle">{QUADRANT_META[quadrant].subtitle}</div>
        </div>
        <span className="count">{items.length}</span>
        <div className="topbar-spacer" />
        <button className="button icon" onClick={() => onAdd(quadrant)}>
          ＋ Add
        </button>
      </header>
      <div className="cards">
        <form
          className="quick-add"
          onSubmit={async (e) => {
            e.preventDefault()
            if (!title.trim()) return
            const value = title.trim()
            setTitle('')
            await onQuickAdd(value, quadrant)
          }}
        >
          <input
            className="field"
            aria-label={`Quick add to ${QUADRANT_META[quadrant].title}`}
            placeholder="Quick add…"
            maxLength={MAX_TITLE}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <button className="button icon" disabled={!title.trim()} aria-label="Add item">
            ↵
          </button>
        </form>
        {projectSuggestions.length > 0 && (
          <div className="quick-project-suggestions" aria-label="Project suggestions">
            {projectSuggestions.map((project) => (
              <button
                type="button"
                key={project.id}
                onClick={() =>
                  setTitle(
                    `${title.slice(0, shorthand?.index ?? title.length).trimEnd()} @${project.slug} `,
                  )
                }
              >
                @{project.slug} <span>{project.name}</span>
              </button>
            ))}
          </div>
        )}
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              onEdit={onEdit}
              onComplete={onComplete}
              onOpenMenu={onOpenMenu}
            />
          ))}
        </SortableContext>
        {!items.length && (
          <div className="empty" style={{ padding: '1rem' }}>
            Nothing here yet
          </div>
        )}
      </div>
    </section>
  )
}

function ItemCard({
  item,
  onEdit,
  onComplete,
  onOpenMenu,
}: {
  item: WorkItem
  onEdit: (item: WorkItem) => void
  onComplete: (item: WorkItem) => void
  onOpenMenu: (item: WorkItem, rect: DOMRect) => void
}) {
  const sortable = useSortable({ id: item.id })
  return (
    <article
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
      className={`card ${sortable.isDragging ? 'dragging' : ''}`}
      data-colour={item.colour}
      {...sortable.attributes}
      onClick={() => onEdit(item)}
    >
      <div className="card-row">
        <input
          type="checkbox"
          aria-label={`Complete ${item.title}`}
          checked={false}
          onChange={() => onComplete(item)}
          onClick={(e) => e.stopPropagation()}
        />
        <button
          className="card-title"
          style={{ border: 0, background: 'transparent', textAlign: 'left', padding: 0 }}
          {...sortable.listeners}
        >
          {item.title}
        </button>
        <button
          className="card-menu-trigger"
          aria-label={`Actions for ${item.title}`}
          aria-haspopup="menu"
          onClick={(e) => {
            e.stopPropagation()
            onOpenMenu(item, e.currentTarget.getBoundingClientRect())
          }}
        >
          •••
        </button>
      </div>
      {item.description && <p className="card-description">{item.description}</p>}
      <TagChips tags={item.tags} />
    </article>
  )
}

function CardActionMenu({
  state,
  onClose,
  onEdit,
  onComplete,
  onDelete,
  onMove,
}: {
  state: Exclude<CardMenuState, null>
  onClose: () => void
  onEdit: (item: WorkItem) => void
  onComplete: (item: WorkItem) => void
  onDelete: (item: WorkItem) => void
  onMove: (item: WorkItem, q: Quadrant) => void
}) {
  const { item } = state
  return (
    <div
      className="menu-popover floating-card-menu"
      style={
        {
          top: `${state.top}px`,
          right: `${state.right}px`,
          maxHeight: `calc(100vh - ${state.top + 8}px)`,
        } as CSSProperties
      }
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <button onClick={() => onEdit(item)}>Edit</button>
      <button onClick={() => onComplete(item)}>Complete</button>
      {QUADRANTS.filter((q) => q !== item.quadrant).map((q) => (
        <button key={q} onClick={() => onMove(item, q)}>
          Move to {QUADRANT_META[q].title}
        </button>
      ))}
      <button style={{ color: '#a82d1e' }} onClick={() => onDelete(item)}>
        Move to trash
      </button>
    </div>
  )
}

function TagChips({ tags }: { tags: Tag[] }) {
  if (!tags.length) return null
  return (
    <div className="tag-row" aria-label="Tags">
      {tags.map((tag) => (
        <span key={tag.id} className="tag-chip" data-colour={tag.colour}>
          {tag.name}
        </span>
      ))}
    </div>
  )
}

function ListView({ items, onEdit }: { items: WorkItem[]; onEdit: (item: WorkItem) => void }) {
  const [quadrant, setQuadrant] = useState('')
  const [colour, setColour] = useState('')
  const [sort, setSort] = useState<'title' | 'updated' | 'quadrant'>('updated')
  const shown = items
    .filter(
      (item) => (!quadrant || item.quadrant === quadrant) && (!colour || item.colour === colour),
    )
    .sort((a, b) =>
      sort === 'title'
        ? a.title.localeCompare(b.title)
        : sort === 'quadrant'
          ? a.quadrant.localeCompare(b.quadrant)
          : b.updatedAt - a.updatedAt,
    )
  return (
    <>
      <div className="toolbar">
        <select
          className="field"
          style={{ width: 'auto' }}
          aria-label="Filter by quadrant"
          value={quadrant}
          onChange={(e) => setQuadrant(e.target.value)}
        >
          <option value="">All quadrants</option>
          {QUADRANTS.map((q) => (
            <option key={q} value={q}>
              {QUADRANT_META[q].title}
            </option>
          ))}
        </select>
        <select
          className="field"
          style={{ width: 'auto' }}
          aria-label="Filter by colour"
          value={colour}
          onChange={(e) => setColour(e.target.value)}
        >
          <option value="">All colours</option>
          {COLOURS.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select
          className="field"
          style={{ width: 'auto' }}
          aria-label="Sort items"
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
        >
          <option value="updated">Recently updated</option>
          <option value="title">Title</option>
          <option value="quadrant">Quadrant</option>
        </select>
      </div>
      <div className="table-wrap">
        <table className="item-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Quadrant</th>
              <th className="optional">Tags</th>
              <th className="optional">Colour</th>
              <th className="optional">Updated</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((item) => (
              <tr
                className="clickable"
                key={item.id}
                onClick={() => onEdit(item)}
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onEdit(item)}
              >
                <td>
                  <strong>{item.title}</strong>
                  {item.description && (
                    <div style={{ color: '#77736a', marginTop: '.2rem' }}>
                      {item.description.slice(0, 120)}
                    </div>
                  )}
                </td>
                <td>{QUADRANT_META[item.quadrant].title}</td>
                <td className="optional">
                  <TagChips tags={item.tags} />
                </td>
                <td className="optional">{item.colour}</td>
                <td className="optional">{new Date(item.updatedAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length && <div className="empty">No matching active items.</div>}
      </div>
    </>
  )
}

function CompletedView({
  items,
  onRestore,
  onDelete,
}: {
  items: WorkItem[]
  onRestore: (item: WorkItem) => void
  onDelete: (item: WorkItem) => void
}) {
  const shown = [...items].sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0))
  return (
    <div className="table-wrap">
      <table className="item-table">
        <thead>
          <tr>
            <th>Completed item</th>
            <th className="optional">Tags</th>
            <th>Completed</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((item) => (
            <tr key={item.id}>
              <td>
                <strong>{item.title}</strong>
                <div style={{ color: '#77736a' }}>{QUADRANT_META[item.quadrant].title}</div>
              </td>
              <td className="optional">
                <TagChips tags={item.tags} />
              </td>
              <td>{item.completedAt ? new Date(item.completedAt).toLocaleString() : '—'}</td>
              <td>
                <button className="button" onClick={() => onRestore(item)}>
                  Restore
                </button>{' '}
                <button className="button danger" onClick={() => onDelete(item)}>
                  Delete permanently
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!shown.length && <div className="empty">Completed items will appear here.</div>}
    </div>
  )
}

const editorSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(MAX_TITLE),
  description: z.string().max(MAX_DESCRIPTION),
  colour: z.enum(COLOURS),
  quadrant: z.enum(QUADRANTS),
  tagIds: z.array(z.string().uuid()).max(20),
  projectId: z.string().uuid().nullable(),
})
type EditorValues = z.infer<typeof editorSchema>
function ItemEditor({
  state,
  onClose,
  onSave,
  tags,
  projects,
  onCreateTag,
}: {
  state: Exclude<EditorState, null>
  onClose: () => void
  onSave: (values: EditorValues) => Promise<void>
  tags: Tag[]
  projects: Project[]
  onCreateTag: (name: string) => Promise<Tag>
}) {
  const item = state.mode === 'edit' ? state.item : null
  const [tagName, setTagName] = useState('')
  const [creatingTag, setCreatingTag] = useState(false)
  const form = useForm<EditorValues>({
    resolver: zodResolver(editorSchema),
    defaultValues: {
      title: item?.title ?? '',
      description: item?.description ?? '',
      colour: item?.colour ?? 'amber',
      quadrant: item?.quadrant ?? (state.mode === 'new' ? state.quadrant : 'DO_FIRST'),
      tagIds: item?.tags.map((tag) => tag.id) ?? [],
      projectId: item?.projectId ?? null,
    },
  })
  const colour = form.watch('colour')
  const selectedTagIds = form.watch('tagIds')
  async function createAndSelectTag() {
    const name = tagName.trim()
    if (!name) return
    setCreatingTag(true)
    try {
      const tag = await onCreateTag(name)
      form.setValue('tagIds', [...new Set([...selectedTagIds, tag.id])], {
        shouldDirty: true,
        shouldValidate: true,
      })
      setTagName('')
    } catch (error) {
      form.setError('root', {
        message: error instanceof Error ? error.message : 'Could not create tag',
      })
    } finally {
      setCreatingTag(false)
    }
  }
  return (
    <div className="panel-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="panel" role="dialog" aria-modal="true" aria-labelledby="editor-title">
        <div className="panel-head">
          <h2 id="editor-title">{item ? 'Edit item' : 'New item'}</h2>
          <button className="button icon" onClick={onClose} aria-label="Close editor">
            ×
          </button>
        </div>
        <form className="form-grid" onSubmit={form.handleSubmit(onSave)}>
          <label className="label">
            Title
            <input autoFocus className="field" maxLength={MAX_TITLE} {...form.register('title')} />
            {form.formState.errors.title && <small>{form.formState.errors.title.message}</small>}
          </label>
          <label className="label">
            Description
            <textarea
              className="field textarea"
              maxLength={MAX_DESCRIPTION}
              {...form.register('description')}
            />
            <span style={{ color: '#77736a', fontWeight: 400 }}>
              {form.watch('description')?.length ?? 0}/{MAX_DESCRIPTION}
            </span>
          </label>
          <fieldset style={{ border: 0, padding: 0 }}>
            <legend className="label" style={{ marginBottom: '.4rem' }}>
              Colour
            </legend>
            <div className="colour-options">
              {COLOURS.map((value) => (
                <button
                  type="button"
                  key={value}
                  className={`colour-option ${colour === value ? 'selected' : ''}`}
                  data-colour={value}
                  aria-label={value}
                  aria-pressed={colour === value}
                  onClick={() =>
                    form.setValue('colour', value as ItemColour, { shouldDirty: true })
                  }
                >
                  {value}
                </button>
              ))}
            </div>
          </fieldset>
          <label className="label">
            Quadrant
            <select className="field" {...form.register('quadrant')}>
              {QUADRANTS.map((q) => (
                <option key={q} value={q}>
                  {QUADRANT_META[q].title} — {QUADRANT_META[q].subtitle}
                </option>
              ))}
            </select>
          </label>
          <label className="label">
            Project
            <select
              className="field"
              value={form.watch('projectId') ?? ''}
              onChange={(event) =>
                form.setValue('projectId', event.target.value || null, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            >
              <option value="">No project</option>
              {projects
                .filter((project) => !project.closedAt || project.id === item?.projectId)
                .map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                    {project.closedAt ? ' (closed)' : ''}
                  </option>
                ))}
            </select>
          </label>
          <fieldset style={{ border: 0, padding: 0 }}>
            <legend className="label" style={{ marginBottom: '.4rem' }}>
              Tags
            </legend>
            <div className="tag-picker">
              {tags.map((tag) => {
                const selected = selectedTagIds.includes(tag.id)
                return (
                  <button
                    type="button"
                    key={tag.id}
                    className={`tag-chip selectable ${selected ? 'selected' : ''}`}
                    data-colour={tag.colour}
                    aria-pressed={selected}
                    onClick={() =>
                      form.setValue(
                        'tagIds',
                        selected
                          ? selectedTagIds.filter((tagId) => tagId !== tag.id)
                          : [...selectedTagIds, tag.id],
                        { shouldDirty: true, shouldValidate: true },
                      )
                    }
                  >
                    {tag.name}
                  </button>
                )
              })}
              {!tags.length && <span className="muted">No tags yet</span>}
            </div>
            <div className="tag-create">
              <input
                className="field"
                value={tagName}
                maxLength={40}
                placeholder="New tag"
                onChange={(e) => setTagName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void createAndSelectTag()
                  }
                }}
              />
              <button
                type="button"
                className="button"
                disabled={creatingTag || !tagName.trim()}
                onClick={() => void createAndSelectTag()}
              >
                {creatingTag ? 'Adding…' : 'Add tag'}
              </button>
            </div>
          </fieldset>
          {form.formState.errors.root && (
            <div className="error-box">{form.formState.errors.root.message}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button primary"
              disabled={form.formState.isSubmitting || creatingTag}
            >
              {form.formState.isSubmitting ? 'Saving…' : item ? 'Save changes' : 'Add item'}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}
